import { webviewLocationAssign, webviewLocationReplace, webviewRuntime } from './webview-runtime'
import { getWebviewQuery } from './webview-query'

export interface WebviewNavigationOptions {
  query?: Record<string, string | boolean | number | undefined | null>
  urlParams?: Record<string, string>
  mode?: 'push' | 'replace'
}

function buildUrl(pathname: string, options?: WebviewNavigationOptions): string {
  let resolvedPath = pathname

  if (options?.urlParams) {
    resolvedPath = resolvedPath.replace(/\$\{(.*?)\}/g, (_, key) => options.urlParams?.[key] ?? '')
  }

  const query = options?.query
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value))
      }
    }
    const qs = params.toString()
    if (qs) {
      resolvedPath += (resolvedPath.includes('?') ? '&' : '?') + qs
    }
  }

  return resolvedPath
}

export const webviewNavigation = {
  /**
   * Navega para uma rota interna.
   * Usa webviewLocationAssign (push) ou webviewLocationReplace (replace).
   * Suporta template params (${key}) e query params.
   * Playground-aware automaticamente.
   */
  navigateTo(pathname: string, options?: WebviewNavigationOptions): void {
    const url = buildUrl(pathname, options)
    if (options?.mode === 'replace') {
      webviewLocationReplace(url)
    } else {
      webviewLocationAssign(url)
    }
  },

  /**
   * Abre URL externa (nova aba/janela).
   * Delega para webviewRuntime.webviewExternalNavigation.
   */
  openExternal(url: string): void {
    webviewRuntime.webviewExternalNavigation({ url })
  },

  /**
   * Envia comando app:// para o app nativo.
   * Esta é a única forma aprovada de usar protocolos app://.
   */
  sendNativeCommand(command: string, params?: Record<string, string>): void {
    let url = 'app://' + command
    if (params) {
      const qs = new URLSearchParams(params).toString()
      if (qs) url += '?' + qs
    }
    webviewLocationAssign(url)
  },

  /**
   * Fecha a webview. Delega para webviewRuntime.webviewFinishMethod().
   */
  finish(): void {
    webviewRuntime.webviewFinishMethod()
  },

  /**
   * Retorna o pathname atual. SSR-safe (retorna '/' no server).
   */
  getPathname(): string {
    if (typeof window === 'undefined') return '/'
    return window.location.pathname
  },

  /**
   * Retorna query params da URL. Delega para getWebviewQuery().
   */
  getQuery<T extends Record<string, string> = Record<string, string>>(): Partial<T> {
    return getWebviewQuery<T>()
  }
}
